import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { metaTitle } from '../../util/tabTitle';
import CanvasEditorV2, {
  EditorTooling,
} from '../../components/canvas-editor-v2/CanvasEditorV2';
import { DialogModal } from '../../components';
import { useGetSlidData, useKvsData, useKvsGroup } from './hooks';
import { CanvasPreviewModalV2 } from '../../components/canvas-preview-v2/CanvasPreviewModalV2';
import { useUpdateSlide } from '../../api-http/slides-v2';
import io, { Socket } from 'socket.io-client';
import { useAuth } from '../../context/auth-context';
import { WS_URL } from '../../configs';
import { Timeout } from './components/SocketTimeout';
import { SOCKET_EVENTS } from '../../util/constant';
import { UserRole } from '../../types/user.types';
import { SlideLock } from './components/UserEdit';
import { handleRefresh } from '../../util/handleRefresh';

let socket: Socket;
const QUERY_KEYWORD = 'slide_edit';
const { refreshSocket, dataConnection, editSlide } = SOCKET_EVENTS;

const CanvasEditorV2Page: FC = () => {
  metaTitle('Canvas Editor');
  const { id: paramId } = useParams<{
    id: string;
  }>();

  const { currentUser, switchedOrgId } = useAuth();

  const [timeoutModal, setTimeoutModal] = useState(false);
  const [socketId, setSocketId] = useState('');
  const [slideLock, setSlideLock] = useState(false);
  const [editor, setEditor] = useState();

  const { slide, projectData, updatedKvs, refetchSlideData } = useGetSlidData(
    paramId || '',
  );

  useEffect(() => {
    if (currentUser && paramId) {
      refetchSlideData();
      if (!socketId || !socket) {
        const { role, organization } = currentUser;

        socket = io(WS_URL, {
          query: {
            keyword: QUERY_KEYWORD,
            slideId: paramId,
            editingBy: currentUser._id,
            organization:
              role === UserRole.superAdmin ? switchedOrgId : organization._id,
          },
        });
      }

      socket.on('connect', () => {
        setSocketId(socket.id);
      });

      socket.on('disconnect', () => {
        setTimeoutModal(true);
      });

      socket.on(dataConnection, (socketData) => {
        const { data, event } = socketData;

        if (data && event === editSlide) {
          if (
            data[0].isEditing &&
            data[0].editingBy._id != currentUser._id &&
            paramId === String(data[0]._id)
          ) {
            setEditor(data[0].editingBy.name);
            socket.disconnect();
            setTimeoutModal(false);
            setSlideLock(true);
          }
        }
      });
    }
  }, [currentUser, paramId, socketId]);

  const { isKvsDataLoading, ...kvsDataProps } = useKvsData();
  const { isKvsGroupsLoading, ...kvsGroupsProps } = useKvsGroup();
  const { mutate, data } = useUpdateSlide();
  const [openPreview, setOpenPreview] = useState(false);
  useEffect(() => {
    if (socketId) {
      socket.emit(refreshSocket, socketId);
    }
  }, [data]);

  useEffect(() => {
    if (!socketId) return;
    const handleRefresh = () => {
      socket.emit(refreshSocket, socketId);
    };
    document.addEventListener('click', handleRefresh);
    return () => {
      document.removeEventListener('click', handleRefresh);
    };
  }, [socketId]);

  const [json, setJson] = useState({});

  // Stable props: CanvasEditorV2 is memoized, so page re-renders (e.g. the
  // loading flag toggling on every save) do not re-render the whole editor.
  const onClickPreview = useCallback((json: object) => {
    // Copy: the preview mutates the objects it gets in place, and they
    // share `data` objects with the live canvas.
    setJson(JSON.parse(JSON.stringify(json)));
    setOpenPreview(true);
  }, []);
  const updateTitle = useMemo(
    () => (slide ? { _id: slide._id, title: slide.title } : undefined),
    [slide?._id, slide?.title],
  );

  const isKvsLoading = isKvsGroupsLoading || isKvsDataLoading;
  if (!slide) {
    return null;
  }

  return (
    <>
      <EditorTooling
        slidesProps={{
          projectData: projectData,
          updatedKvs: updatedKvs,
          slide,
          updateSlide: mutate,
          paramId,
        }}
        kvsProps={{
          ...kvsDataProps,
          ...kvsGroupsProps,
          isKvsLoading,
        }}
        openPreview={openPreview}
      >
        <CanvasEditorV2
          realHeight={slide?.projectData?.deviceInfo?.height || 1080}
          realWidth={slide?.projectData?.deviceInfo?.width || 1920}
          onClickPreview={onClickPreview}
          updateTitle={updateTitle}
          socket={socket}
        />
      </EditorTooling>
      <DialogModal
        title="Session expired"
        onClose={() => handleRefresh()}
        open={timeoutModal}
      >
        <Timeout refresh={() => handleRefresh()} />
      </DialogModal>

      <DialogModal
        title="Slide locked"
        onClose={() => handleRefresh()}
        open={slideLock}
      >
        <SlideLock
          editingUser={editor || 'User'}
          resetSocket={() => handleRefresh()}
        />
      </DialogModal>

      <CanvasPreviewModalV2
        title="Preview"
        onClose={() => setOpenPreview(false)}
        open={openPreview}
        json={json}
      />
    </>
  );
};

export default CanvasEditorV2Page;
